import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { orderService } from '../../services/order.service';
import { theme } from '../../config/theme';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { PackageIcon, SearchIcon } from '../../components/icons/Icons';

const STATUS_TABS = [
  { key: '', label: 'All Orders' },
  { key: 'processing', label: 'Processing' },
  { key: 'packed', label: 'Packed' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'out_for_delivery', label: 'Out for Delivery' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
];

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  const label = status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: theme.borderRadius.full,
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.semibold,
      color: '#fff',
      backgroundColor: color,
      textTransform: 'capitalize',
    }}>
      {label}
    </span>
  );
};

const Orders = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });

  const fetchOrders = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (activeTab) params.status = activeTab;
      const response = await orderService.getUserOrders(params);
      const data = response.data?.data || response.data;
      setOrders(data.orders || []);
      setPagination({
        page: data.pagination?.page || 1,
        totalPages: data.pagination?.totalPages || 1,
      });
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchOrders(1);
  }, [fetchOrders]);

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? theme.spacing.md : theme.spacing.xl, maxWidth: '1100px', margin: '0 auto' }}>
        <h1 style={{
          fontSize: isMobile ? theme.typography.fontSize['2xl'] : theme.typography.fontSize['3xl'],
          fontWeight: theme.typography.fontWeight.bold,
          marginBottom: theme.spacing.xl,
        }}>
          My Orders
        </h1>

        {/* Status Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: theme.spacing.sm,
          marginBottom: theme.spacing.xl,
          overflowX: 'auto',
          paddingBottom: theme.spacing.sm,
        }}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                borderRadius: theme.borderRadius.full,
                border: `1px solid ${activeTab === tab.key ? theme.colors.primary.main : theme.colors.gray[300]}`,
                backgroundColor: activeTab === tab.key ? theme.colors.primary.main : 'transparent',
                color: activeTab === tab.key ? '#fff' : theme.colors.text.secondary,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: theme.transitions.fast,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Orders List */}
        {loading ? (
          <LoadingSpinner />
        ) : orders.length === 0 ? (
          <Card padding="xl">
            <div style={{ textAlign: 'center', padding: theme.spacing['2xl'] }}>
              <PackageIcon size={64} color={theme.colors.gray[300]} />
              <h3 style={{ marginTop: theme.spacing.lg, color: theme.colors.text.secondary }}>
                No orders found
              </h3>
              <p style={{ color: theme.colors.text.disabled, marginBottom: theme.spacing.lg }}>
                {activeTab ? 'No orders match this filter.' : "You haven't placed any orders yet."}
              </p>
              <Button variant="primary" onClick={() => navigate('/')}>
                Start Shopping
              </Button>
            </div>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
            {orders.map(order => (
              <Card key={order.id} padding="lg" hoverable onClick={() => navigate(`/orders/${order.id}`)}>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between',
                  gap: theme.spacing.md,
                }}>
                  {/* Left: Order info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginBottom: theme.spacing.sm, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: theme.typography.fontSize.base,
                        fontWeight: theme.typography.fontWeight.semibold,
                        color: theme.colors.text.primary,
                      }}>
                        #{order.order_number}
                      </span>
                      {(() => {
                        if (order.status === 'cancelled' || order.status === 'refunded') {
                          return <StatusBadge status={order.status} />;
                        }
                        
                        // Calculate an aggregate fulfillment status across all items
                        if (order.items && order.items.length > 0) {
                          const statuses = order.items.map(i => i.fulfillment_status);
                          
                          // If all items share the exact same status, use it
                          if (statuses.every(s => s === statuses[0])) {
                             return <StatusBadge status={statuses[0]} />;
                          }
                          
                          // Otherwise, find the "lowest" progress status among them
                          const FULFILLMENT_STEPS = ['pending', 'processing', 'packed', 'shipped', 'out_for_delivery', 'delivered'];
                          const lowestIdx = Math.min(...statuses.map(s => FULFILLMENT_STEPS.indexOf(s)).filter(idx => idx !== -1));
                          const aggregateStatus = lowestIdx !== Infinity ? FULFILLMENT_STEPS[lowestIdx] : order.status;
                          
                          return <StatusBadge status={aggregateStatus} />;
                        }
                        
                        return <StatusBadge status={order.status} />;
                      })()}
                    </div>
                    <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, marginBottom: theme.spacing.sm }}>
                      Placed on {formatDate(order.createdAt || order.created_at, 'long')}
                    </div>

                    {/* Product thumbnails */}
                    {order.items && order.items.length > 0 && (
                      <div style={{ display: 'flex', gap: theme.spacing.sm, marginTop: theme.spacing.sm, flexWrap: 'wrap' }}>
                        {order.items.slice(0, 4).map(item => (
                          <div key={item.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.xs,
                            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                            backgroundColor: theme.colors.gray[50],
                            borderRadius: theme.borderRadius.md,
                            fontSize: theme.typography.fontSize.xs,
                          }}>
                            {item.product_image && (
                              <img
                                src={item.product_image}
                                alt={item.product_name}
                                style={{ width: 24, height: 24, borderRadius: theme.borderRadius.sm, objectFit: 'cover' }}
                              />
                            )}
                            <span>{item.product_name} × {item.quantity}</span>
                          </div>
                        ))}
                        {order.items.length > 4 && (
                          <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.disabled, alignSelf: 'center' }}>
                            +{order.items.length - 4} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right: Amount + Action */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isMobile ? 'flex-start' : 'flex-end',
                    justifyContent: 'center',
                    gap: theme.spacing.sm,
                    minWidth: isMobile ? 'auto' : '150px',
                  }}>
                    <div style={{
                      fontSize: theme.typography.fontSize.xl,
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.colors.text.primary,
                    }}>
                      {formatCurrency(order.total)}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/orders/${order.id}`);
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </Card>
            ))}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: theme.spacing.sm, marginTop: theme.spacing.lg }}>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchOrders(pagination.page - 1)}
                >
                  Previous
                </Button>
                <span style={{ alignSelf: 'center', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchOrders(pagination.page + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Orders;
